import dayjs from "dayjs";

// =====================
// Seed Helpers
// =====================
function stableHashToUnit(str) {
  return stableHash(str) / 4294967296;
}

function stableHash(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return hash >>> 0;
}

function mixUint32(a, b) {
  let x = (a ^ b) >>> 0;
  x = Math.imul(x, 0x9e3779b1) >>> 0;
  x ^= x >>> 16;
  return x >>> 0;
}

function seededInt(seed, min, max) {
  const u = stableHashToUnit(seed);
  return min + Math.floor(u * (max - min + 1));
}

function seededPick(seed, list) {
  if (!Array.isArray(list) || list.length === 0) return null;
  return list[seededInt(seed, 0, list.length - 1)];
}

const companyPrefixes = [
  "North",
  "South",
  "East",
  "West",
  "Blue",
  "Green",
  "Red",
  "Silver",
  "Golden",
  "Prime",
  "Vertex",
  "Summit",
  "Pioneer",
  "Apex",
  "Nimbus",
  "Nova",
  "Orbit",
  "Quantum",
  "Fusion",
  "Zenith",
  "Aurora",
  "Cascade",
  "Cedar",
  "Cobalt",
  "Crown",
  "Harbor",
  "Horizon",
  "Iron",
  "Maple",
  "Oak",
  "Pebble",
  "Rocket",
  "Saffron",
  "Sierra",
  "Skyline",
  "Stone",
  "Sunrise",
  "Timber",
  "Union",
  "Violet",
  "Willow",
];

const companyRoots = [
  "Systems",
  "Solutions",
  "Technologies",
  "Networks",
  "Digital",
  "Software",
  "Analytics",
  "Dynamics",
  "Platforms",
  "Ventures",
  "Holdings",
  "Logistics",
  "Security",
  "Consulting",
  "Engineering",
  "Industries",
  "Labs",
  "Studios",
  "Works",
  "Capital",
  "Partners",
  "Health",
  "Energy",
  "Retail",
  "Media",
  "Finance",
  "Cloud",
  "Data",
  "AI",
  "Mobile",
];

const companySuffixes = [
  "Group",
  "Labs",
  "Systems",
  "Solutions",
  "Tech",
  "Works",
  "Partners",
  "Ventures",
  "Studios",
  "Networks",
  "Digital",
];

const companyLegal = ["Inc", "LLC", "Ltd", "Corp"];

function generateCompanyName(seed) {
  const prefix = seededPick(`${seed}|prefix`, companyPrefixes);
  const root = seededPick(`${seed}|root`, companyRoots);
  const suffix = seededPick(`${seed}|suffix`, companySuffixes);
  const includeLegal = stableHashToUnit(`${seed}|legal`) < 0.35;
  const legal = includeLegal
    ? seededPick(`${seed}|legalPick`, companyLegal)
    : "";

  const pattern = seededInt(`${seed}|pattern`, 1, 4);
  const base =
    pattern === 1
      ? `${prefix} ${root}`
      : pattern === 2
        ? `${root} ${suffix}`
        : pattern === 3
          ? `${prefix} ${root} ${suffix}`
          : `${root}`;

  return legal ? `${base} ${legal}` : base;
}

// =====================
// Stable Company List
// =====================
function createStableCompanies(count, seed) {
  const usedNames = new Set();
  return Array.from({ length: count }, (_, i) => {
    const id = 100 + i;
    let attempt = 0;
    let client = "";
    while (attempt < 50) {
      const next = generateCompanyName(`${seed}|company|${id}|name|${attempt}`);
      if (!usedNames.has(next)) {
        client = next;
        usedNames.add(next);
        break;
      }
      attempt++;
    }
    if (!client) client = generateCompanyName(`${seed}|company|${id}|fallback`);
    return { id, client };
  });
}

function pickDailyCompanies(pool, seed, date, count, prevKey) {
  const dayHash = stableHash(`${seed}|${date}|pick`);
  const scored = pool
    .map((c) => ({
      c,
      s: mixUint32(dayHash, stableHash(`${seed}|company|${c.id}|pick`)),
    }))
    .sort((a, b) => a.s - b.s || a.c.id - b.c.id);

  const safeCount = Math.min(Math.max(0, count), scored.length);
  let companies = scored.slice(0, safeCount).map((x) => x.c);
  let key = companies
    .map((c) => c.id)
    .sort((a, b) => a - b)
    .join(",");

  if (prevKey && key === prevKey && scored.length > safeCount) {
    companies = companies
      .slice(0, Math.max(0, safeCount - 1))
      .concat(scored[safeCount].c);
    key = companies
      .map((c) => c.id)
      .sort((a, b) => a - b)
      .join(",");
  }

  return { companies, key };
}

function clampInt(n, min, max) {
  return Math.min(max, Math.max(min, Math.trunc(n)));
}

function splitTotalIntoParts(total, parts) {
  const keys = Object.keys(parts);
  const weights = keys.map((k) => Math.max(0, Number(parts[k] ?? 0)));
  const weightSum = weights.reduce((a, b) => a + b, 0);
  if (total <= 0 || weightSum <= 0) {
    return keys.reduce((acc, k) => ({ ...acc, [k]: 0 }), {});
  }

  const scaled = weights.map((w) => (w / weightSum) * total);
  const floors = scaled.map((x) => Math.floor(x));
  let remaining = total - floors.reduce((a, b) => a + b, 0);

  const order = scaled
    .map((x, idx) => ({ idx, frac: x - floors[idx] }))
    .sort((a, b) => b.frac - a.frac)
    .map((x) => x.idx);

  for (let i = 0; i < order.length && remaining > 0; i++) {
    floors[order[i]] += 1;
    remaining -= 1;
  }

  return keys.reduce((acc, k, idx) => ({ ...acc, [k]: floors[idx] }), {});
}

// =====================
// MAIN FUNCTION
// =====================
export function generateDateWiseData({
  startDate = "2025-10-01",
  endDate = dayjs().format("YYYY-MM-DD"),
  companyCount = 5,
  minCompanyCount,
  maxCompanyCount,
  poolSize,
  seed = "kultworks-aging",
  minOutstanding = 0,
  maxOutstanding = 12600,
} = {}) {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const totalDays = end.diff(start, "day");
  if (totalDays < 0) return [];

  const maxCount = clampInt(
    maxCompanyCount ?? companyCount,
    1,
    Number.MAX_SAFE_INTEGER,
  );
  const minCount = clampInt(
    minCompanyCount ?? Math.max(1, Math.floor(maxCount * 0.6)),
    1,
    maxCount,
  );
  const poolCount = clampInt(
    poolSize ?? Math.max(30, maxCount * 6),
    maxCount,
    Number.MAX_SAFE_INTEGER,
  );
  const companyPool = createStableCompanies(poolCount, seed);
  let prevPickedKey = null;

  return Array.from({ length: totalDays + 1 }, (_, d) => {
    const date = start.add(d, "day").format("YYYY-MM-DD");
    const dailyCount = seededInt(
      `${seed}|${date}|dailyCount`,
      minCount,
      maxCount,
    );
    const picked = pickDailyCompanies(
      companyPool,
      seed,
      date,
      dailyCount,
      prevPickedKey,
    );
    prevPickedKey = picked.key;

    const companyData = picked.companies.map((comp) => {
      const daySeed = `${seed}|${date}|${comp.id}`;

      const hasBalance = stableHashToUnit(`${daySeed}|hasBalance`) >= 0.15;
      const baseMin = clampInt(minOutstanding, 0, Number.MAX_SAFE_INTEGER);
      const baseMax = clampInt(
        maxOutstanding,
        baseMin,
        Number.MAX_SAFE_INTEGER,
      );
      const companyBase = seededInt(
        `${seed}|company|${comp.id}|baseTotal`,
        clampInt(baseMax * 0.3, baseMin, baseMax),
        baseMax,
      );
      const dailyMultiplier = 0.75 + stableHashToUnit(`${daySeed}|mult`) * 0.5;
      const totalOutstanding = hasBalance
        ? clampInt(companyBase * dailyMultiplier, baseMin, baseMax)
        : 0;

      const profile = seededInt(`${seed}|company|${comp.id}|profile`, 0, 3);
      const profileBase =
        profile === 0
          ? {
              current: 0.55,
              "1to30": 0.25,
              "31to60": 0.12,
              "61to90": 0.06,
              "91to120": 0.02,
            }
          : profile === 1
            ? {
                current: 0.35,
                "1to30": 0.4,
                "31to60": 0.15,
                "61to90": 0.07,
                "91to120": 0.03,
              }
            : profile === 2
              ? {
                  current: 0.25,
                  "1to30": 0.25,
                  "31to60": 0.2,
                  "61to90": 0.15,
                  "91to120": 0.15,
                }
              : {
                  current: 0.15,
                  "1to30": 0.2,
                  "31to60": 0.25,
                  "61to90": 0.2,
                  "91to120": 0.2,
                };

      const jittered = Object.keys(profileBase).reduce((acc, k) => {
        const jitter = 0.7 + stableHashToUnit(`${daySeed}|w|${k}`) * 0.6;
        return { ...acc, [k]: profileBase[k] * jitter };
      }, {});

      const bucket = splitTotalIntoParts(totalOutstanding, jittered);

      return {
        id: comp.id, // ✅ same every day
        client: comp.client, // ✅ same every day
        ...bucket,
        action:
          stableHashToUnit(`${seed}|company|${comp.id}|action`) > 0.5
            ? "Funded"
            : "Non Funded",
      };
    });

    return {
      date,
      company: companyData,
    };
  });
}
